// src/pages/IdentityVerification.js
import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import { CameraAlt, CheckCircle, CloudUpload } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { uploadData } from "../services/request";

const IdentityVerification = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // File Select Handler
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setSnackbar({
          open: true,
          message: "Please select an image file",
          severity: "warning",
        });
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        setSelectedImage(e.target.result);
        setSelectedFile(file);
      };
      reader.readAsDataURL(file);
    }
  };

  // Upload Button Click
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // Done Button
  const handleDone = async () => {
    if (!selectedImage || !selectedFile) {
      setSnackbar({
        open: true,
        message: "Please upload your selfie first",
        severity: "warning",
      });
      return;
    }

    setLoading(true);
    try {
      const accessToken = localStorage.getItem("accessToken");
      const storedUserData = localStorage.getItem("userData");
      const userData = storedUserData ? JSON.parse(storedUserData) : null;
      const email = userData?.email;

      if (!accessToken) {
        setSnackbar({
          open: true,
          message: "Your session has expired. Please log in again.",
          severity: "error",
        });
        return;
      }

      if (!email) {
        setSnackbar({
          open: true,
          message: "User email is missing. Please log in again.",
          severity: "error",
        });
        return;
      }

      // ✅ 1. FormData banayein (Actual file object ko append karein)
      const formData = new FormData();
      formData.append("selfie", selectedFile); // File object (name nahi)
      formData.append("email", email); // Extra data agar backend chahiye

      // ✅ 2. API Call karein (Token ke sath)
      const response = await uploadData(
        "/api/auth/identity-verification",
        formData,
        accessToken,
      );

      // ✅ 3. Backend `verified: true` return karta hai, `success` nahi!
      if (response?.verified === false) {
        throw new Error(response?.message || "Upload failed");
      }

      setSnackbar({
        open: true,
        message: "Identity verified successfully!",
        severity: "success",
      });
      setTimeout(() => navigate("/payment"), 1500);
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: "Upload failed. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        // minHeight: "92vh",

        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #f9f5ff 0%, #ffffff 100%)",
        py: 2,
      }}
    >
      <Box
        sx={{
          width: { xs: "90%", sm: 480 },
          backgroundColor: "#fff",
          borderRadius: "32px",
          padding: { xs: "30px", sm: "40px" },
          boxShadow: "0px 25px 80px rgba(82, 5, 123, 0.15)",
          textAlign: "center",
        }}
      >
        {/* Header */}
        <Typography
          variant="h5"
          sx={{
            color: "#52057b",
            fontWeight: "bold",
            fontSize: "1.7rem",
            mb: 4,
          }}
        >
          Identity Verification
        </Typography>

        <Typography
          variant="h6"
          sx={{ color: "#52057b", fontWeight: "600", mb: 1.5 }}
        >
          Verify your Identity
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: "#555",
            lineHeight: 1.7,
            fontSize: "0.95rem",
            mb: 4,
          }}
        >
          We'll need you to verify your identity by taking a clear selfie
          showing your face. This helps prevent fraud. Please take the picture
          in a well lit area.
        </Typography>

        {/* Camera / Upload Box */}
        <Box
          onClick={handleUploadClick}
          sx={{
            width: "100%",
            maxWidth: "280px",
            height: "280px",
            margin: "0 auto",
            position: "relative",
            cursor: "pointer",
            borderRadius: "24px",
            backgroundColor: "#f3e8ff",
            border: "2px dashed #52057b",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 4,
            overflow: "hidden",
            transition: "all 0.3s ease",
            "&:hover": {
              borderColor: "#3e055e",
              backgroundColor: "#efe0ff",
              boxShadow: "0px 10px 30px rgba(82, 5, 123, 0.15)",
            },
          }}
        >
          {/* Hidden File Input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleFileSelect}
          />

          {selectedImage ? (
            <>
              <img
                src={selectedImage}
                alt="Selfie Preview"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "22px",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: "10px",
                  right: "10px",
                  backgroundColor: "#fff",
                  borderRadius: "50%",
                  padding: "6px",
                  boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
                }}
              >
                <CheckCircle sx={{ color: "#4caf50", fontSize: "2rem" }} />
              </Box>
            </>
          ) : (
            <Box sx={{ textAlign: "center", color: "#52057b" }}>
              <CloudUpload sx={{ fontSize: "4rem", mb: 1, opacity: 0.8 }} />
              <Typography
                sx={{ color: "#52057b", fontWeight: 600, fontSize: "1rem" }}
              >
                Tap to upload selfie
              </Typography>
              <Typography sx={{ color: "#888", fontSize: "0.8rem", mt: 0.5 }}>
                JPG, PNG or WEBP
              </Typography>
            </Box>
          )}
        </Box>

        {/* Done Button */}
        <Button
          fullWidth
          variant="contained"
          onClick={handleDone}
          disabled={!selectedImage || loading}
          sx={{
            bgcolor: "#52057b",
            color: "#fff",
            py: 2,
            borderRadius: "16px",
            fontWeight: "bold",
            fontSize: "1.1rem",
            textTransform: "none",
            boxShadow: "0px 10px 25px rgba(82, 5, 123, 0.3)",
            "&:hover": {
              bgcolor: "#3e055e",
              boxShadow: "0px 15px 35px rgba(82, 5, 123, 0.4)",
              transform: "translateY(-2px)",
            },
            "&.Mui-disabled": {
              bgcolor: "#e0e0e0",
              color: "#9e9e9e",
              boxShadow: "none",
            },
          }}
        >
          {loading ? (
            <CircularProgress size={24} sx={{ color: "#fff" }} />
          ) : (
            "Done"
          )}
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          sx={{ width: "100%", borderRadius: "12px" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default IdentityVerification;
